import type { ChangeEventHandler } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Box, FormHelperText, FormLabel, Group, Textarea, Typography } from '@screentone/core';

import { ResponsiveLoader } from 'components';
import { useAlert } from 'contexts/alert/useAlert';
import { PublicationSettingUpdateInput } from 'data/generated/graphql';
import PublicationSettingsActionBar from 'features/publication-settings-common/PublicationSettingsActionBar';
import usePublicationSettingsLock from 'features/publication-settings-common/usePublicationSettingsLock';
import { useConvertedProperty } from 'hooks';
import { usePublicationSettings, useSavePublicationSetting } from 'hooks/publication-settings';
import { useJsonField } from 'hooks/useJsonField';

const PublicationSettingsPublishingRules = () => {
  const [currentPublicationSetting, setCurrentPublicationSetting] = useState<PublicationSettingUpdateInput | null>(
    null
  );
  const [hasChanged, setHasChanged] = useState(false);

  const currentProperty = useConvertedProperty();
  const { alertError, alertSuccess } = useAlert();
  const { publishingFragment, lockIsReady, lockLoading, isPageLockingEnabled, handleLock, handleRelease } =
    usePublicationSettingsLock();

  const publicationSettingsQueryVariables = { publicationKey: currentProperty ?? '' };
  const { data, isLoading: isLoad, fetchStatus } = usePublicationSettings(publicationSettingsQueryVariables);
  const isLoading = isLoad && fetchStatus !== 'idle';

  // The save puts the saved settings in the cache, so the form reloads from them, with their new version.
  const { mutate, isLoading: isUpdating } = useSavePublicationSetting();

  const allowRepublishWhenDirty = useJsonField(data?.publicationSetting.allowRepublishWhenDirty);

  const jsonFields = useMemo(() => [allowRepublishWhenDirty], [allowRepublishWhenDirty]);

  useEffect(() => {
    if (data) {
      setCurrentPublicationSetting(data.publicationSetting);
    }
  }, [data]);

  const handleInputStringChange =
    (fieldName: keyof PublicationSettingUpdateInput): ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> =>
    (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (currentPublicationSetting === null) {
        return;
      }
      const { value } = e.target as HTMLInputElement | HTMLTextAreaElement;
      setCurrentPublicationSetting({ ...currentPublicationSetting, [fieldName]: value });
      setHasChanged(true);
    };

  const handleJsonFieldChange =
    (jsonField: { handleChange: (value: string) => void }): ChangeEventHandler<HTMLTextAreaElement> =>
    (e: React.FormEvent<HTMLTextAreaElement>) => {
      jsonField.handleChange((e.target as HTMLTextAreaElement).value);
      setHasChanged(true);
    };

  const buildPublicationSettingInput = (): PublicationSettingUpdateInput => ({
    ...currentPublicationSetting!,
    allowRepublishWhenDirty: allowRepublishWhenDirty.parseValue()?.getJSON()
  });

  const handleSave = () => {
    if (currentPublicationSetting === null || !currentProperty) {
      return;
    }

    let newPublicationSetting: PublicationSettingUpdateInput;
    try {
      newPublicationSetting = buildPublicationSettingInput();
    } catch (err) {
      alertError(`Cannot save — fix the highlighted JSON field(s) first. (${(err as Error).message})`);
      return;
    }

    mutate(
      {
        publicationSettingUpdateInput: newPublicationSetting
      },
      {
        onSuccess: () => {
          setHasChanged(false);
          jsonFields.forEach((field) => field.clearError());
          alertSuccess('Publication settings saved successfully');
        },
        onError: (error) => {
          alertError(`Failed to save publication settings. ${(error as Error).message}`);
        }
      }
    );
  };

  const { isReadOnly } = publishingFragment;

  return (
    <Group gap="lg" direction="column">
      {!currentPublicationSetting ? (
        <ResponsiveLoader />
      ) : (
        <>
          <Group.Item fullWidth>
            <PublicationSettingsActionBar
              onSave={handleSave}
              hasChanged={hasChanged}
              isSaving={isUpdating}
              isLoading={isLoading}
              publishingFragment={publishingFragment}
              lockIsReady={lockIsReady}
              isPageLockingEnabled={isPageLockingEnabled}
              lockLoading={lockLoading}
              onLock={handleLock}
              onRelease={handleRelease}
            />
          </Group.Item>

          <Group.Item fullWidth>
            <Box>
              <Box.Title>Publishing rules</Box.Title>
              <Box.Content>
                <Typography variant="note">
                  Rules the publish pipeline applies before writing to Allesseh: dirty-collection republishing behavior.
                  Get these wrong and content either fails to publish or republishes when it shouldn&apos;t.
                </Typography>
                <Group gap="mlg" direction="column" fullWidth margin={{ top: 'md' }}>
                  <Group gap="xs" direction="column" fullWidth>
                    <FormLabel
                      label="Whitelist of Republish When Dirty Collections"
                      labelPosition="top"
                      fullWidth
                    />
                    <FormHelperText>
                      JSON of entity keys that must carry the <code>republish_when_dirty</code> flag so downstream
                      collections auto-republish. Use for collections whose parent artifacts change frequently.
                    </FormHelperText>
                    <Textarea
                      placeholder="Enter an object defining which entities must be marked with republish_when_dirty flag"
                      value={allowRepublishWhenDirty.stringValue}
                      onChange={handleJsonFieldChange(allowRepublishWhenDirty)}
                      error={!!allowRepublishWhenDirty.error}
                      disabled={isReadOnly}
                    />
                    {!!allowRepublishWhenDirty.error && (
                      <FormHelperText error>{allowRepublishWhenDirty.error}</FormHelperText>
                    )}
                  </Group>
                  <Group gap="xs" direction="column" fullWidth>
                    <FormLabel
                      label="Ignore Republishing of Dirty Collections"
                      labelPosition="top"
                      fullWidth
                    />
                    <FormHelperText>
                      Comma-separated list of entities to skip when republishing dirty collections. Use &quot;*&quot; to
                      disable republish-on-dirty globally for this brand.
                    </FormHelperText>
                    <Textarea
                      placeholder="Enter a comma separated list of entities that should be ignored from republishing when marked as republish_when_dirty. Enter a '*' to ignore all entities"
                      value={
                        currentPublicationSetting.ignoreRepublishing
                          ? typeof currentPublicationSetting.ignoreRepublishing === 'string'
                            ? currentPublicationSetting.ignoreRepublishing
                            : JSON.stringify(currentPublicationSetting.ignoreRepublishing)
                          : ''
                      }
                      onChange={handleInputStringChange('ignoreRepublishing')}
                      disabled={isReadOnly}
                    />
                  </Group>
                </Group>
              </Box.Content>
            </Box>
          </Group.Item>
        </>
      )}
    </Group>
  );
};

export default PublicationSettingsPublishingRules;
